import { Objects } from "./objects";
import { Strings } from "./Strings";

export class Arrays {
  /**
   * 배열의 가운데를 기준으로 좌우를 뒤바꾼다.
   * 배열의 길이가 짝수면 좌변과 우변이 뒤바꾸고,
   * 홀수면 가운데 요소는 그대로 둔 상태로 좌변과 우변을 뒤바꾼다.
   * <li>1,2,3,4,5,6 => 4,5,6,1,2,3
   * <li>1,2,3,4,5 => 4,5,3,1,2
   * @param a 바꿀 배열
   * @return
   */
  public static switchInPlace(a: any[]): any[] {
    if (a.length <= 1) return a;

    let l: number = a.length;
    let p: number = l / 2;
    let t: any[];

    if (l % 2) {
      t = a.slice(p + 1);
      a = t.concat(a.slice(p, p + 1)).concat(a.slice(0, p));
    } else {
      t = a.slice(p);
      a = t.concat(a.slice(0, p));
    }

    return a;
  }

  /**
   * 배열의 길이는 그대로 두고, 배열의 모든 요소를 하나씩 좌로 이동시킨다.
   * 이때 배열의 끝점에 위치한 요소는 다른 끝점으로 이동하게 된다.
   * <li>1,2,3,4,5,6 => 2,3,4,5,6,1
   * @param a 순환시킬 배열
   * @param times 반복 횟수
   * @return
   */
  public static rotate(a: any[], times: number = 1): any[] {
    if (a.length > 1) {
      while (times--) {
        a.push(a.shift());
      }
    }

    return a;
  }

  /**
   * 배열의 길이는 그대로 두고, 배열의 모든 요소를 하나씩 우로 이동시킨다.
   * 이때 배열의 끝점에 위치한 요소는 다른 끝점으로 이동하게 된다.
   * <li>1,2,3,4,5,6 => 6,1,2,3,4,5
   * @param a 순환시킬 배열
   * @param times 반복 횟수
   * @return
   */
  public static rotateReverse(a: any[], times: number = 1): any[] {
    if (a.length > 1) {
      while (times--) {
        a.unshift(a.pop());
      }
    }

    return a;
  }

  /**
   * 전달된 배열의 사본을 반환한다.(deepCopy)
   * @param a 사본을 만들 배열
   * @return
   */
  public static deepCopy(a: any[]): any[] {
    return Objects.copy(a);
  }

  public static getIndex(source: any[], item: any, keys: string[]): number {
    if (!source || source.length == 0 || !item || !keys || keys.length == 0)
      return -1;

    let index: number = -1;

    for (let i: number = 0; i < source.length; i++) {
      let eq: boolean = true;

      keys.forEach((key: string) => {
        let s: any = source[i];

        if (!s.hasOwnProperty(key) || s[key] !== item[key]) {
          eq = false;
          return;
        }
      });

      if (eq) {
        index = i;
        return;
      }
    }

    return index;
  }

  public static isEmpty(a: any[]): boolean {
    return a == undefined || !a || a.length === 0;
  }

  public static isNotEmpty(a: any[]): boolean {
    return !this.isEmpty(a);
  }

  /**
   * 첫번째 배열과 두번째 배열이 순서에 관계 없이 같은 요소(또는 키 값)를 가지는지 검사
   * <pre>
   * ex)
   *     null == null 	= throw Exception
   *     null == {1, 2, 3} 	= false
   *     {1, 2, 3} == null 	= false
   *     {1, 2, 3} == {2, 3}	= false
   * </pre>
   * @param srcA 첫번째 배열
   * @param srcB 두번째 배열
   * @param key 요소간 동등여부 비교시 비교대상이 되는 속성 명.
   * 이 값이 전달되면 객체간 비교시 해당 속성의 값만을 동등(==)연산자로 비교하며,
   * 이 값이 전달되지 않으면 객체간 동등비교 한다.
   * @return 첫번째 배열과 두번째 배열의 모든 요소(키가 정해진 경우 해당 요소내 속성)가 동등하면 True, 그렇지 않으면 False
   * @Error 첫번째와 두번째 배열이 모두 null 일때
   */
  public static equalsIgnoreOrder(
    srcA: any[],
    srcB: any[],
    key: string
  ): boolean {
    if (!srcA && !srcB) {
      throw new Error("All comparison targets are null.");
    }

    if (!srcA || !srcB) {
      return false;
    }

    if (srcA.length === 0 && srcB.length === 0) {
      return true;
    }

    if (srcA.length !== srcB.length) {
      return false;
    }

    return this.containsAll(srcA, srcB, key);
  }

  /**
   * 첫번째 배열의 모든 요소가 두번째 배열에 포함되어 있는지 여부를 검사
   * <pre>
   * ex)
   *     null == null 				= throw Exception
   *     null == {1, 2, 3} 				= false
   *     {1, 2, 3} == null 				= false
   *     {"1", "2", "3"} == {1,2,3} 			= false
   *     {"a", "b", "c"} == {"c", "b", "a"} 		= true
   * </pre>
   * @param srcA 첫번째 배열
   * @param srcB 두번째 배열
   * @param key 요소간 동등여부 비교시 비교대상이 되는 속성 명.
   * 이 값이 전달되면 객체간 비교시 해당 속성의 값만을 동등(==)연산자로 비교하며,
   * 이 값이 전달되지 않으면 객체간 동등비교 한다.
   * @return 첫번째 배열의 모든 요소가 두번째 배열에 포함되어 있으면 True, 그렇지 않으면 False
   * @Error 첫번째와 두번째 배열이 모두 null 일때
   */
  public static containsAll(srcA: any[], srcB: any[], key: string): boolean {
    if (!srcA && !srcB) {
      throw new Error("All comparison targets are null.");
    }

    if (!srcA || !srcB) {
      return false;
    }

    if (srcA.length > srcB.length) {
      return true;
    }

    let hasKey: boolean = !Strings.isBlank(key);
    let result: boolean = false;

    srcA.forEach((o) => {
      let found: boolean = false;
      let a: any = hasKey ? o[key] : o;

      srcB.forEach((p) => {
        let b: any = hasKey ? p[key] : p;

        if (a === b) {
          result = found = true;
          return;
        }
      });

      if (!found) {
        result = false;
        return;
      }
    });

    return result;
  }

  /**
   * <li>두 배열의 길이가 같은지 판단하는 메소드</li>
   * <li>다차원 배열의 길이는 무시됩니다.</li>
   * <li>null로 전달받은 인자는 배열의 길이가 0과 같이 판단됩니다.</li>
   * <pre>
   * ex)
   *     null == null				= true
   *     [] == []		 			= true
   *     null == [] 				= true
   *     [] == null	 			= true
   *     [1, 2, 3] == [1, 2, 4] 	= true
   *     [1, 2, 3] == [1, 4] 		= false
   *     [1, [3, 4, 5]] == [1, 3]	= true
   * </pre>
   * <p>다차원 배열의 길이는 무시됩니다.</p>
   * @param srcA 첫번째 배열
   * @param srcB 두번째 배열
   * @return 두 배열의 길이가 같은경우 true, 다른경우 false
   */
  public static isSameLength(srcA: any[], srcB: any[]): boolean {
    if (
      (!this.isEmpty(srcA) && this.isEmpty(srcB)) ||
      (this.isEmpty(srcA) && !this.isEmpty(srcB)) ||
      (!this.isEmpty(srcA) && !this.isEmpty(srcB) && srcA.length != srcB.length)
    )
      return false;

    return true;
  }

  public static move(src: any[], oldIndex: number, newIndex: number): any[] {

    if (!src)
      return src;

    if (oldIndex < 0 || newIndex > src.length)
      return src;

    src.splice(newIndex, 0, src.splice(oldIndex, 1)[0]);
    return src;
  }

  public static removeItem(src: any[], item: any): any[] {
    let idx: number = this.getItemIndex(src, item);
    
    if (idx !== -1)
      src.splice(idx, 1);
    
    return src;
  }

  public static getItemIndex(src: any[], item: any): number {

    if (!src)
      return -1;

    return src.findIndex((e: any) => e === item);

  }

  public static safeGetValue(src: any[], idx: number, defaultValue: any = null): any {

    if (this.isEmpty(src))
      return defaultValue;
    
    if (src.length - 1 < idx) {
      console.warn('is indexOutofbounds exception:', src, idx);
      return defaultValue;
    }

    return src[idx];
  }
}
